

import React, { useContext, useRef, useState, useLayoutEffect, useEffect } from 'react';
import axios from '../axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Menubar } from 'primereact/menubar';
import { InputText } from 'primereact/inputtext';

import { useMountEffect, useUpdateEffect, useIntersectionObserver, useLocalStorage } from 'primereact/hooks';

import moment from 'moment';
import { classNames } from "primereact/utils";
import { Skeleton } from 'primereact/skeleton';
import LayoutContext from '../Context/LayoutContext';
import { Button } from 'primereact/button';
import ReportAddModal from '../Modals/ReportAddModal';

import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import AppContext from '../Context/AppContext';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const Reports = () => {
    const URL = window.location.protocol + "//" + window.location.hostname + process.env.REACT_APP_API_APPENDIX

    const [reports, setReports] = useState([]);
    const [skeleton, setSkeleton] = useState(0);
    const [loading, setLoading] = useState(false);

    const [addModal, setAddModal] = useState(false);

    const dataParams = useRef({ offset: 0, limit: 16, count: 0, searchText: "" })
    const lastElementRef = useRef(null);
    const lastElementVisible = useIntersectionObserver(lastElementRef);
    const scrollPosition = useRef(0);
    const networkController = useRef(null)
    const scrollRef = useRef(null)

    const [searchText, setSearchText] = useLocalStorage("", "security.reports.search")

    const { setNavbar } = useContext(LayoutContext)
    const { isDesktop } = useContext(AppContext)

    const [isFullScreen, setIsFullScreen] = useState(false);
    const [currendID, setCurrentID] = useState(null);

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    useEffect(() => {
        setNavbar(
            <div className='flex gap-2'>
                <div className="p-inputgroup max-w-20rem flex-1">
                    <span className="p-inputgroup-addon">
                        <i className="pi pi-search"></i>
                    </span>
                    <InputText placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                </div>
                <Button className="white-space-nowrap" label={"Add Report"} onClick={() => setAddModal(true)} />
            </div>
        )
    }, [searchText])

    useMountEffect(() => {
        fetchData({ ...dataParams.current }, true);
    });

    useUpdateEffect(() => {
        if (!lastElementVisible)
            return;

        fetchData({ ...dataParams.current }, true)
    }, [lastElementVisible])

    useUpdateEffect(() => {
        dataParams.current.searchText = searchText

        fetchData({ ...dataParams.current, offset: 0 }, skeleton !== 0)
    }, [searchText]);

    const fetchData = async (params, skeleton = false) => {
        try {
            networkController.current?.abort()
            skeleton ? setSkeleton(params.limit) : setLoading(true)

            networkController.current = new AbortController()
            const response = await axios.get('/reports', { signal: networkController.current.signal, params: params });

            const _reports = [...reports.slice(0, params.offset), ...response.data.data]
            setReports(_reports)

            dataParams.current = { ...dataParams.current, count: response.data.count, offset: params.offset + response.data.data.length }

            setSkeleton(0)
            setLoading(false)
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') {
                console.log("Request Canceled!");
            } else {
                console.error('Error:', error);
            }
        }
    };

    const indexTemplate = (value, props) => {
        return value ? props.rowIndex + 1 : <Skeleton height="26px" borderRadius="16px"></Skeleton>;
    }

    const officerTemplate = (value) => {
        return value ? value.officer : <Skeleton height="26px" borderRadius="16px"></Skeleton>;
    };

    const dateTemplate = (value) => {
        return value ? moment(value.date).format(isDesktop ? "DD MMMM YYYY hh:mm A" : "DD MMMM YYYY") : <Skeleton height="26px" borderRadius="16px"></Skeleton>;
    };

    useLayoutEffect(() => {
        scrollRef.current.scrollTo({
            top: scrollPosition.current,
            left: 0,
            behavior: "instant",
        });
    }, [reports]);

    const handleScroll = (event) => {
        scrollPosition.current = event.target.scrollTop
    }

    const handlePrint = async (id) => {
        try {
            const response = await axios.get('/reports/download', {
                params: { id: id }
            });
            const { filename, filedata } = response.data.data;
            // Convert base64 to a Blob
            const byteCharacters = atob(filedata);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });

            // Create an object URL from the Blob
            const blobUrl = URL.createObjectURL(blob);

            // Open the URL in a new window
            const printWindow = window.open(blobUrl);
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.focus();
                    printWindow.print();
                };
            }
        } catch (error) {
            console.error('Error downloading the file:', error);
        }
    }

    const handleDownload = async (id) => {
        try {
            const response = await axios.get('/reports/download', {
                params: { id: id }
            });
            const { filename, filedata } = response.data.data;

            // Decode base64 data and create a blob
            const byteCharacters = atob(filedata);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/octet-stream' });

            // Create a link element
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename); // Use the filename from the response

            // Append to the document and trigger the download
            document.body.appendChild(link);
            link.click();

            // Clean up and remove the link
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error('Error downloading the file:', error);
        }
    };

    const action = (value) => {
        return value ?
            <div className='flex gap-2'>
                <i className="pi pi-eye text-xl p-1 icon-button" style={{ color: 'var(--primary-color)' }} onClick={() => { setCurrentID(value.id); toggleFullScreen() }}></i>
                <i className="pi pi-download text-xl p-1 icon-button" style={{ color: 'var(--purple-500)' }} onClick={() => { handleDownload(value.id) }}></i>
                <i className="pi pi-print text-xl p-1 icon-button" style={{ color: 'var(--info-color)' }} onClick={() => { handlePrint(value.id) }}></i>
            </div>
            : "";
    }

    const [numPages, setNumPages] = useState(0);
    function onDocumentLoadSuccess({ numPages: nextNumPages }) {
        setNumPages(nextNumPages);
    }

    return (
        <div className="flex flex-column flex-auto surface-section overflow-scroll align-items-center" ref={scrollRef} onScroll={handleScroll}>
            <div className="w-full datacontent flex-1 container relative px-0 md:px-4">
                <DataTable size={"small"} value={reports.concat(Array(skeleton).fill(null))} className="users-table surface-ground reports-table" scrollable scrollHeight="flex">
                    <Column field="name" header="" body={indexTemplate} pt={{ bodyCell: { className: 'pl-3 md:pl-5' } }}></Column>
                    <Column field="officer" header="Officer(s)" body={officerTemplate} className='w-6' pt={{ bodyCell: { style: {whiteSpace: "nowrap", maxWidth: "0px", overflow: "hidden", textOverflow: "ellipsis"}}}}></Column>
                    <Column field="date" header="Date" body={dateTemplate} className='w-6' pt={{ bodyCell: { style: {whiteSpace: "nowrap"}}}}></Column>
                    <Column body={action}/>
                </DataTable>
                {(!loading && skeleton === 0) && <div className={classNames("absolute bottom-0 w-1rem h-20rem", lastElementVisible || dataParams.current.count === dataParams.current.offset ? "hidden" : "")} ref={lastElementRef}></div>}
            </div>

            {addModal && <ReportAddModal hide={() => setAddModal(false)} update={() => {
                fetchData({ ...dataParams.current, offset: 0, limit: dataParams.current.offset + 1 })
            }}></ReportAddModal>}

            {isFullScreen && (
                <div className="pdf-overlay">
                    <button className="absolute top-0 right-0 z-5 m-6" onClick={toggleFullScreen}>Close</button>
                    <div className='max-h-full overflow-scroll'>
                        <Document className="flex flex-column" file={`${URL}/reports/display?id=${currendID}`} onLoadSuccess={onDocumentLoadSuccess}>
                            {Array.from(new Array(numPages), (el, index) => (
                                <Page className="my-3" key={`page_${index + 1}`} pageNumber={index + 1} scale={0.6}/>
                            ))}
                        </Document>
                    </div>
                </div>
            )}
        </div>
    );
};

/*
<Document file={`http://localhost:3001/reports/display?id=${currendID}`} onLoadSuccess={onDocumentLoadSuccess}>
                        {Array.from(new Array(numPages), (el, index) => (
                            <Page key={`page_${index + 1}`} pageNumber={index + 1} />
                        ))}
                    </Document>
*/

export default Reports;